<template>
  <v-dialog v-model="dialog" max-width="600px" persistent>
    <v-card>
      <v-card-title class="headline grey lighten-2">
        {{ $t('colleges.create_period') }}
      </v-card-title>

      <v-form ref="form" v-model="valid" lazy-validation>
        <v-card-text class="pt-6">
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-alert type="info" dense outlined>
                  {{ $t('colleges.creating_custom_period_info') }}
                </v-alert>
              </v-col>

              <v-col v-if="validationMessage" cols="12">
                <v-alert type="warning" dense>
                  {{ $t(`colleges.${validationMessage}`, validationParams) }}
                </v-alert>
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="formData.name"
                  :label="$t('colleges.period_name')"
                  :rules="fieldRequired"
                  outlined
                  dense
                  :disabled="loading"
                  placeholder="e.g., Summer School 2025"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.startDate"
                  :label="$t('colleges.start_date')"
                  :rules="startDateRules"
                  type="date"
                  outlined
                  dense
                  :disabled="loading"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.endDate"
                  :label="$t('colleges.end_date')"
                  :rules="endDateRules"
                  type="date"
                  outlined
                  dense
                  :disabled="loading"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="grey darken-1"
            :disabled="loading"
            @click="onCancel"
          >
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="loading"
            :disabled="!valid"
            @click="onSave"
          >
            {{ $t('common.create') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import LoadingMixin from '@/mixins/loading';
import SnackbarMixin from '@/mixins/snackbar';
import academicPeriodService from '@/services/academicPeriod.service';
import {
  validateStartBeforeEnd,
  validateNoOverlap,
} from '@/utils/academicPeriodValidations';
import DateUtils from '@/utils/date.utils';

export default {
  name: 'CreateAcademicPeriodModal',
  mixins: [LoadingMixin, SnackbarMixin],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    schoolId: {
      type: [String, Number],
      required: true,
    },
    periods: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    valid: true,
    formData: {
      name: '',
      startDate: '',
      endDate: '',
    },
    validationMessage: '',
    validationParams: {},
    dateValidationEnabled: false,
  }),
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    currentDefaultPeriod() {
      // Find the active or most recently ended default period.
      // Excludes future periods (startDate > today) so that an upcoming period
      // does not block creation of custom periods that fall before it starts.
      const today = new Date();
      const defaultPeriods = this.periods.filter(
        (p) => p.calendarTypeId !== null && new Date(p.startDate) <= today,
      );

      if (defaultPeriods.length === 0) return null;

      // Among periods that have already started, prefer the one active today;
      // fall back to the one that ended most recently.
      const active = defaultPeriods.find(
        (p) => new Date(p.startDate) <= today && new Date(p.endDate) >= today,
      );
      if (active) return active;

      return defaultPeriods.sort((a, b) => new Date(b.endDate) - new Date(a.endDate))[0];
    },
    nextDefaultPeriod() {
      // Find the next default period after the current one.
      // When there is no currentDefaultPeriod (all defaults are in the future),
      // use the earliest upcoming default period so the end-date ceiling still applies.
      const today = new Date();

      if (this.currentDefaultPeriod) {
        const defaultPeriods = this.periods
          .filter((p) => p.calendarTypeId !== null && p.id !== this.currentDefaultPeriod.id)
          .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

        return defaultPeriods.find(
          (p) => new Date(p.startDate) > new Date(this.currentDefaultPeriod.endDate),
        ) || null;
      }

      // No active/past default period — use the earliest future default period
      // as the upper boundary so the custom period cannot overlap with it.
      const futurePeriods = this.periods
        .filter((p) => p.calendarTypeId !== null && new Date(p.startDate) > today)
        .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

      return futurePeriods[0] || null;
    },
    fieldRequired() {
      return [(v) => !!v || this.$t('common.field_required')];
    },
    startDateRules() {
      return [
        (v) => !!v || this.$t('common.field_required'),
        (v) => this.validateStartDate(v, this.formData.endDate),
      ];
    },
    endDateRules() {
      return [
        (v) => !!v || this.$t('common.field_required'),
        (v) => this.validateEndDate(this.formData.startDate, v),
      ];
    },
  },
  watch: {
    dialog(newVal) {
      if (!newVal) {
        this.$refs.form.reset();
        this.formData = {
          name: '',
          startDate: '',
          endDate: '',
        };
        this.resetDateValidationState();
      }
    },
    'formData.startDate': function (newVal) {
      this.applyDateValidationMessage(newVal, this.formData.endDate);
    },
    'formData.endDate': function (newVal) {
      this.applyDateValidationMessage(this.formData.startDate, newVal);
    },
  },
  methods: {
    resetDateValidationState() {
      this.dateValidationEnabled = false;
      this.validationMessage = '';
      this.validationParams = {};
    },
    applyDateValidationMessage(
      startDate = this.formData.startDate,
      endDate = this.formData.endDate,
    ) {
      const startValidation = this.runStartDateValidation(startDate, endDate);
      if (!startValidation.isValid) {
        this.validationMessage = startValidation.message || '';
        this.validationParams = startValidation.params || {};
        return startValidation;
      }

      const endValidation = this.runEndDateValidation(startDate, endDate);
      if (!endValidation.isValid) {
        this.validationMessage = endValidation.message || '';
        this.validationParams = endValidation.params || {};
        return endValidation;
      }

      this.validationMessage = '';
      this.validationParams = {};
      return { isValid: true };
    },
    runStartDateValidation(startDate, endDate) {
      if (!startDate) {
        return { isValid: true };
      }

      if (this.currentDefaultPeriod) {
        const normalizedStart = DateUtils.normalizeDateToYYYYMMDD(startDate);
        const normalizedCurrentEnd = DateUtils.normalizeDateToYYYYMMDD(
          this.currentDefaultPeriod.endDate,
        );

        if (normalizedStart && normalizedCurrentEnd && normalizedStart < normalizedCurrentEnd) {
          return {
            isValid: false,
            message: 'validation_custom_start',
            params: {
              endDate: normalizedCurrentEnd,
              periodName: this.currentDefaultPeriod.name,
            },
          };
        }
      }

      if (endDate) {
        const basicValidation = validateStartBeforeEnd(startDate, endDate);
        if (!basicValidation.isValid) {
          return { isValid: false, message: basicValidation.message };
        }
      }

      return { isValid: true };
    },
    validateEndDateAgainstNextPeriod(endDate) {
      const normalizedEnd = DateUtils.normalizeDateToYYYYMMDD(endDate);
      if (!normalizedEnd) return { isValid: true };

      if (!this.currentDefaultPeriod) {
        // No active/past default period — only guard against overlapping the next future one.
        if (!this.nextDefaultPeriod) return { isValid: true };

        const normalizedNextStart = DateUtils.normalizeDateToYYYYMMDD(
          this.nextDefaultPeriod.startDate,
        );
        if (normalizedNextStart && normalizedEnd >= normalizedNextStart) {
          return { isValid: false, message: 'validation_custom_end' };
        }
        return { isValid: true };
      }

      // There is a current period — use nextDefaultPeriod.startDate + 1 year as the ceiling.
      const nextStartDate = this.nextDefaultPeriod
        ? this.nextDefaultPeriod.startDate
        : new Date(new Date().setFullYear(new Date().getFullYear() + 10)).toISOString();

      const normalizedNextStart = DateUtils.normalizeDateToYYYYMMDD(nextStartDate);

      if (normalizedNextStart) {
        const [year, month, day] = normalizedNextStart.split('-').map(Number);
        const nextStartPlusYearStr = `${year + 1}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

        if (normalizedEnd >= nextStartPlusYearStr) {
          return { isValid: false, message: 'validation_custom_end' };
        }
      }
      return { isValid: true };
    },
    runEndDateValidation(startDate, endDate) {
      if (!endDate) {
        return { isValid: true };
      }

      if (!startDate) {
        return this.validateEndDateAgainstNextPeriod(endDate);
      }

      const basicValidation = validateStartBeforeEnd(startDate, endDate);
      if (!basicValidation.isValid) {
        return { isValid: false, message: basicValidation.message };
      }

      const nextPeriodValidation = this.validateEndDateAgainstNextPeriod(endDate);
      if (!nextPeriodValidation.isValid) {
        return nextPeriodValidation;
      }

      return validateNoOverlap({ startDate, endDate }, this.periods);
    },
    validateStartDate(startDate, endDate) {
      if (!this.dateValidationEnabled) {
        this.applyDateValidationMessage(startDate, endDate);
        return true;
      }

      const validation = this.runStartDateValidation(startDate, endDate);
      this.applyDateValidationMessage(startDate, endDate);

      if (!validation.isValid) {
        return this.$t(`colleges.${validation.message}`, validation.params);
      }

      return true;
    },
    validateEndDate(startDate, endDate) {
      if (!this.dateValidationEnabled) {
        this.applyDateValidationMessage(startDate, endDate);
        return true;
      }

      const validation = this.runEndDateValidation(startDate, endDate);
      this.applyDateValidationMessage(startDate, endDate);

      if (!validation.isValid) {
        return this.$t(`colleges.${validation.message}`, validation.params);
      }

      return true;
    },
    onCancel() {
      this.dialog = false;
      this.$refs.form.reset();
      this.resetDateValidationState();
    },
    async onSave() {
      this.dateValidationEnabled = true;
      if (!this.$refs.form.validate()) {
        return;
      }

      try {
        this.loader();
        const payload = {
          name: this.formData.name,
          startDate: DateUtils.normalizeDateToYYYYMMDD(this.formData.startDate) || '',
          endDate: DateUtils.normalizeDateToYYYYMMDD(this.formData.endDate) || '',
          campusId: Number(this.schoolId),
          calendarTypeId: null, // Custom periods don't have a calendar type
        };

        const response = await academicPeriodService.create(payload);

        if (response.status === 200 || response.status === 201) {
          this.snackbarSuccess(this.$t('colleges.period_created_success'));
          this.$emit('period-created', response.data);
          this.dialog = false;
          this.$refs.form.reset();
          this.resetDateValidationState();
        }
      } catch (error) {
        this.snackbarError(this.$t('colleges.period_error'));
      } finally {
        this.loader();
      }
    },
  },
};
</script>
